import { useConversationSidePanelContext } from "@app/components/assistant/conversation/ConversationSidePanelContext";
import { DEFAULT_RIGHT_PANEL_SIZE } from "@app/components/assistant/conversation/constant";
import { useDesktopNavigation } from "@app/components/navigation/DesktopNavigationContext";
import { useHashParam } from "@app/hooks/useHashParams";
import { FULL_SCREEN_HASH_PARAM } from "@app/types/conversation_side_panel";
import { useCallback, useEffect, useRef } from "react";

// Grows the conversation side panel to the whole window and back, keeping the panel's own chrome
// (close, back, exit full screen). The state lives in the URL hash so a reload keeps it.
export function usePanelFullScreen({
  exitOnEscape = true,
}: {
  exitOnEscape?: boolean;
} = {}) {
  const { panelRef } = useConversationSidePanelContext();
  const { isNavigationBarOpen, setIsNavigationBarOpen } =
    useDesktopNavigation();
  const isNavBarPrevOpenRef = useRef(isNavigationBarOpen);
  const prevPanelSizeRef = useRef(DEFAULT_RIGHT_PANEL_SIZE);
  const [fullScreenHash, setFullScreenHash] = useHashParam(
    FULL_SCREEN_HASH_PARAM
  );
  const isFullScreen = fullScreenHash === "true";
  // eslint-disable-next-line react-hooks/refs
  const panel = panelRef?.current;

  const restoreLayout = useCallback(() => {
    if (panel) {
      setIsNavigationBarOpen(isNavBarPrevOpenRef.current ?? true);
      panel.resize(prevPanelSizeRef.current ?? DEFAULT_RIGHT_PANEL_SIZE);
    }
  }, [panel, setIsNavigationBarOpen]);

  const exitFullScreen = useCallback(() => {
    setFullScreenHash(undefined);
  }, [setFullScreenHash]);

  const enterFullScreen = () => {
    isNavBarPrevOpenRef.current = isNavigationBarOpen;
    if (panel) {
      prevPanelSizeRef.current = panel.getSize();
    }
    setFullScreenHash("true");
  };

  // Leaving the panel (close or back) must also put the layout back.
  const leaveFullScreen = () => {
    if (panel && isFullScreen) {
      setFullScreenHash(undefined);
      restoreLayout();
    }
  };

  useEffect(() => {
    if (!panel) {
      return;
    }
    if (isFullScreen) {
      panel.resize(100);
      setIsNavigationBarOpen(false);
    } else if (panel.getSize() === 100 && !isNavigationBarOpen) {
      // Only undo full screen when the layout is still the full-screen one.
      restoreLayout();
    }
  }, [
    panel,
    isFullScreen,
    isNavigationBarOpen,
    setIsNavigationBarOpen,
    restoreLayout,
  ]);

  useEffect(() => {
    if (!exitOnEscape) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isFullScreen) {
        exitFullScreen();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [exitOnEscape, isFullScreen, exitFullScreen]);

  return { isFullScreen, enterFullScreen, exitFullScreen, leaveFullScreen };
}
